import React, { useState, useContext, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Fragment } from "react";
import { toast } from "react-toastify";
import MarketNavbar from "../../MarketNav/MarketNavbar";
import MarketplacePage from "../../components/MarketplacePage";
import CheckoutAuthStrip from "../../components/CheckoutAuthStrip";
import CartItemPayment from "./CartItemPayment";
import PaymentMethodSelector from "../../components/PaymentMethodSelector";
import useFormData from "../../../../Context/UseFormData";
import { CartContext } from "../../../../Context/CartContext";
import { AuthContext } from "../../../../Context/AuthContext";
import useCheckout, {
  CHECKOUT_COMPLETED_KEY,
  GUEST_CHECKOUT_EMAIL_KEY,
} from "../../libs/useCheckout";
import { clearServerCart, syncCartToServer } from "../../libs/cartSyncApi";
import { refreshCartStockLevels, recoverFromStockError } from "../../libs/validateCartStock";
import { parseStockErrorMessage } from "../../libs/cartStockMessages";
import { useAddresses } from "../../libs/useAddresses";
import useMarketplaceShippingFees, {
  getShippingCostFromSettings,
} from "../../libs/useMarketplaceShippingFees";
import usePayOnDeliveryEligibility from "../../libs/usePayOnDeliveryEligibility";
import { NIGERIAN_STATES } from "../../libs/categoryRoutes";
import StexSelect from "../../components/StexSelect";
import "../../components/StexSelect.css";
import "./CartPayment.css";
import "./checkout.css";

const STATE_OPTIONS = NIGERIAN_STATES.map((state) => ({ value: state, label: state }));

const ShippingDetail = () => {
  const [formData, handleChange, setFormData] = useFormData("shippingDetailFormData");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("door");
  const [paymentMethod, setPaymentMethod] = useState("paystack");
  const { cartItems, replaceCartItems, clearAllCart, totalCart } = useContext(CartContext);
  const { token, isLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const { mutate: checkout } = useCheckout();
  const { data: shippingSettings } = useMarketplaceShippingFees();
  const { data: addresses = [] } = useAddresses();

  const shippingCost = getShippingCostFromSettings(shippingSettings, shippingMethod);
  const orderTotal = totalCart + shippingCost;

  const { data: podEligibility, isFetching: checkingPod } = usePayOnDeliveryEligibility(
    formData.email,
    formData.phone_number,
    orderTotal
  );

  const pickupFee = getShippingCostFromSettings(shippingSettings, "pickup");
  const doorFee = getShippingCostFromSettings(shippingSettings, "door");
  const pickupLabel = shippingSettings?.pickupLabel || "Pickup station";
  const doorLabel = shippingSettings?.doorLabel || "Door delivery";

  const podEligible = podEligibility?.eligible !== false;
  const podBlockedMessage = podEligibility?.eligible === false ? podEligibility.message : null;
  const podMaxAmount = podEligibility?.maxAmount ?? shippingSettings?.payOnDeliveryMaxAmount;
  const podBlockDays = podEligibility?.blockDays ?? shippingSettings?.payOnDeliveryBlockDays ?? 60;
  const podEnabled = shippingSettings?.payOnDeliveryEnabled !== false;

  const canUsePod = useMemo(
    () =>
      isLoggedIn &&
      podEnabled &&
      podEligible &&
      shippingMethod === "door" &&
      (podMaxAmount == null || orderTotal <= podMaxAmount),
    [isLoggedIn, podEnabled, podEligible, shippingMethod, podMaxAmount, orderTotal]
  );

  useEffect(() => {
    if (!canUsePod && paymentMethod === "pay_on_delivery") {
      setPaymentMethod("paystack");
    }
  }, [canUsePod, paymentMethod]);

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate("/market/cart/empty", { replace: true });
    }
  }, [cartItems.length, navigate]);

  const applyAddress = (addressId) => {
    const addr = addresses.find((a) => a._id === addressId);
    if (!addr) return;
    setFormData({
      full_name: addr.full_name,
      email: addr.email,
      phone_number: addr.phone_number,
      second_phone: addr.second_phone || "",
      city: addr.city,
      state: addr.state,
      address: addr.address,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }

    if (paymentMethod === "pay_on_delivery" && !canUsePod) {
      toast.error(podBlockedMessage || "Pay on delivery is not available for this order.");
      return;
    }

    setIsSubmitting(true);

    let stockCheck;
    try {
      stockCheck = await refreshCartStockLevels(cartItems, replaceCartItems);
    } catch (err) {
      toast.error("Could not verify product stock. Please try again.");
      setIsSubmitting(false);
      return;
    }

    if (!stockCheck.ok || stockCheck.items.length === 0) {
      toast.error(
        stockCheck.messages?.[0] ||
          "Some items in your cart are unavailable. Please update your cart."
      );
      setIsSubmitting(false);
      return;
    }

    if (stockCheck.hadChanges) {
      stockCheck.messages.forEach((msg) => toast.warn(msg, { autoClose: 8000 }));
      toast.info("Your cart was updated to match stock. Go back to your cart, review items, then return here.");
      setIsSubmitting(false);
      return;
    }

    try {
      if (isLoggedIn && token) {
        await syncCartToServer(stockCheck.items, token);
      }
    } catch (err) {
      const { message } = await recoverFromStockError(err, stockCheck.items, replaceCartItems);
      toast.error(message, { autoClose: 10000 });
      setIsSubmitting(false);
      return;
    }

    const checkoutData = {
      items: stockCheck.items.map((item) => ({
        productId: item._id,
        quantity: item.quantity,
        size: item.selectedSize || undefined,
      })),
      shippingMethod,
      paymentMethod,
      shippingAddress: {
        full_name: formData.full_name,
        email: formData.email,
        phone_number: formData.phone_number,
        second_phone: formData.second_phone || "",
        city: formData.city,
        state: formData.state,
        address: formData.address,
      },
    };

    checkout(checkoutData, {
      onSuccess: (data) => {
        if (!isLoggedIn && formData.email) {
          sessionStorage.setItem(
            GUEST_CHECKOUT_EMAIL_KEY,
            formData.email.trim().toLowerCase()
          );
        }

        if (data.paymentMethod === "pay_on_delivery" || !data.paymentLink) {
          sessionStorage.setItem(CHECKOUT_COMPLETED_KEY, "1");
          clearAllCart();
          if (token) {
            clearServerCart(token).catch(() => {});
          }
          navigate(`/market/order-confirmation/${data.orderId}`);
          return;
        }

        if (data.paymentLink) {
          window.location.href = data.paymentLink;
        } else {
          toast.error("Payment link not received.");
          setIsSubmitting(false);
        }
      },
      onError: (error) => {
        const raw = error.response?.data?.message || "";
        toast.error(
          parseStockErrorMessage(raw) || raw || "Checkout failed. Please try again.",
          { autoClose: 10000 }
        );
        setIsSubmitting(false);
      },
    });
  };

  const submitLabel =
    isSubmitting
      ? "Processing…"
      : paymentMethod === "pay_on_delivery"
        ? "Place order"
        : "Pay now";

  return (
    <Fragment>
      <MarketNavbar />
      <MarketplacePage className="marketplace-page--checkout">
        <div className="checkout-page">
          <nav className="checkout-steps" aria-label="Checkout progress">
            <Link to="/market/cart">Bag</Link>
            <span className="checkout-steps__sep">/</span>
            <strong>Checkout</strong>
          </nav>

          <div className="checkout-header">
            <h1>Checkout</h1>
            {!isLoggedIn && <CheckoutAuthStrip returnUrl="/market/checkout" />}
          </div>

          {isLoggedIn && addresses.length > 0 && (
            <div className="checkout-saved-address">
              <label htmlFor="saved-address">Saved address</label>
              <select id="saved-address" onChange={(e) => applyAddress(e.target.value)} defaultValue="">
                <option value="" disabled>
                  Choose a saved address
                </option>
                {addresses.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.label} — {a.full_name}, {a.city}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="checkout-layout">
            <div className="checkout-main">
              <form id="checkout-form" className="checkout-panel" onSubmit={handleSubmit}>
                <div className="checkout-section">
                  <h2 className="checkout-section__title">Contact</h2>
                  <div className="checkout-field-row">
                    <div className="checkout-field">
                      <label htmlFor="full_name">Full name</label>
                      <input
                        id="full_name"
                        type="text"
                        name="full_name"
                        value={formData.full_name || ""}
                        onChange={handleChange}
                        required
                        autoComplete="name"
                      />
                    </div>
                    <div className="checkout-field">
                      <label htmlFor="email">Email</label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>
                  <div className="checkout-field-row">
                    <div className="checkout-field">
                      <label htmlFor="phone_number">Phone</label>
                      <input
                        id="phone_number"
                        type="tel"
                        name="phone_number"
                        value={formData.phone_number || ""}
                        onChange={handleChange}
                        required
                        minLength={10}
                        maxLength={15}
                        placeholder="08012345678"
                        autoComplete="tel"
                      />
                    </div>
                    <div className="checkout-field">
                      <label htmlFor="second_phone">Alt. phone (optional)</label>
                      <input
                        id="second_phone"
                        type="tel"
                        name="second_phone"
                        value={formData.second_phone || ""}
                        onChange={handleChange}
                        autoComplete="tel"
                      />
                    </div>
                  </div>
                </div>

                <div className="checkout-section">
                  <h2 className="checkout-section__title">Delivery</h2>
                  <div className="checkout-field-row">
                    <div className="checkout-field">
                      <label htmlFor="city">City</label>
                      <input
                        id="city"
                        type="text"
                        name="city"
                        value={formData.city || ""}
                        onChange={handleChange}
                        required
                        autoComplete="address-level2"
                      />
                    </div>
                    <div className="checkout-field checkout-field--select">
                      <StexSelect
                        id="state"
                        name="state"
                        label="State"
                        value={formData.state || ""}
                        onChange={handleChange}
                        options={STATE_OPTIONS}
                        placeholder="Select state"
                        required
                        searchable
                      />
                    </div>
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="address">Street address</label>
                    <input
                      id="address"
                      type="text"
                      name="address"
                      value={formData.address || ""}
                      onChange={handleChange}
                      required
                      minLength={5}
                      placeholder="Street, area, landmark"
                      autoComplete="street-address"
                    />
                  </div>
                </div>

                <div className="checkout-section">
                  <h2 className="checkout-section__title">Shipping method</h2>
                  <div className="shipping-method-cards">
                    <label
                      className={`shipping-method-card${
                        shippingMethod === "pickup" ? " shipping-method-card--active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="shippingMethod"
                        value="pickup"
                        checked={shippingMethod === "pickup"}
                        onChange={() => setShippingMethod("pickup")}
                      />
                      <span className="shipping-method-card__label">{pickupLabel}</span>
                      <span className="shipping-method-card__price">₦{pickupFee.toLocaleString()}</span>
                    </label>
                    <label
                      className={`shipping-method-card${
                        shippingMethod === "door" ? " shipping-method-card--active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="shippingMethod"
                        value="door"
                        checked={shippingMethod === "door"}
                        onChange={() => setShippingMethod("door")}
                      />
                      <span className="shipping-method-card__label">{doorLabel}</span>
                      <span className="shipping-method-card__price">₦{doorFee.toLocaleString()}</span>
                    </label>
                  </div>
                </div>

                <PaymentMethodSelector
                  paymentMethod={paymentMethod}
                  onChange={setPaymentMethod}
                  shippingMethod={shippingMethod}
                  podEligible={podEligible && !checkingPod}
                  podEnabled={podEnabled}
                  podBlockedMessage={podBlockedMessage}
                  podMaxAmount={podMaxAmount}
                  podBlockDays={podBlockDays}
                  orderTotal={orderTotal}
                  isLoggedIn={isLoggedIn}
                />
              </form>
            </div>

            <aside className="checkout-aside">
              <CartItemPayment
                shippingMethod={shippingMethod}
                shippingSettings={shippingSettings}
                paymentMethod={paymentMethod}
              />
              <button
                type="submit"
                form="checkout-form"
                className="checkout-submit"
                disabled={isSubmitting || checkingPod}
              >
                {submitLabel}
              </button>
            </aside>
          </div>
        </div>
      </MarketplacePage>
    </Fragment>
  );
};

export default ShippingDetail;
